import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  MessageSquare, FileQuestion, Workflow, BarChart3, Settings, 
  CreditCard, Users, ShieldCheck, Bell, LogOut, Menu, X, 
  ChevronDown, Globe, Building2, Palette, FlaskConical, User, HelpCircle, Sparkles,
  LayoutDashboard, Phone, Activity, ExternalLink, Calendar as CalendarIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth, useLanguage, API } from '../App';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import OnboardingWelcomeModal from '../components/OnboardingWelcomeModal';
import DemoModeBanner from '../components/DemoModeBanner';
import { useDemoMode } from '../contexts/DemoModeContext';

const DashboardLayout = () => {
  const { user, logout, token } = useAuth();
  const { language, changeLanguage } = useLanguage();
  const demoMode = useDemoMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [organization, setOrganization] = useState(null);
  const [debugStatus, setDebugStatus] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const notifPrefsRef = useRef({
    desktop_notifications_enabled: true,
    desktop_new_conversation: true,
    desktop_customer_message: true,
    desktop_conversation_escalated: true,
  });
  const lastNotifTimeRef = useRef({});  // conversation_id -> timestamp (debounce)
  const NOTIF_DEBOUNCE_MS = 2000;

  // Load notification preferences
  useEffect(() => {
    if (!token) return;
    const loadPrefs = async () => {
      try {
        const resp = await axios.get(`${API}/settings/notification-preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        notifPrefsRef.current = resp.data;
      } catch (e) {
        // Use defaults
      }
    };
    loadPrefs();
  }, [token]);

  // Request browser notification permission on mount
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!token) return;
    
    const connectWS = () => {
      try {
        const wsBase = API.replace(/^https/, 'wss').replace(/^http/, 'ws');
        const wsUrl = `${wsBase}/ws/events?token=${token}`;
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('[WS] Connected');
          // Ping every 30s to keep alive
          ws._pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) ws.send('ping');
          }, 30000);
        };
        
        ws.onmessage = (event) => {
          if (event.data === 'pong') return;
          try {
            const data = JSON.parse(event.data);
            handleWSEvent(data);
          } catch (e) {
            // ignore non-JSON
          }
        };
        
        ws.onclose = () => {
          if (ws._pingInterval) clearInterval(ws._pingInterval);
          // Reconnect after 5s
          reconnectTimerRef.current = setTimeout(connectWS, 5000);
        };
        
        ws.onerror = () => { ws.close(); };
        
        wsRef.current = ws;
      } catch (e) {
        console.warn('[WS] Connection error:', e);
      }
    };
    
    connectWS();
    
    return () => {
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [token]);
  
  const showDesktopNotification = useCallback((title, body, conversationId) => {
    // Guards: browser support, permission, tab hidden, master switch
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    if (!document.hidden) return;
    if (!notifPrefsRef.current.desktop_notifications_enabled) return;

    // Debounce: one notification per conversation per 2s
    if (conversationId) {
      const now = Date.now();
      const last = lastNotifTimeRef.current[conversationId] || 0;
      if (now - last < NOTIF_DEBOUNCE_MS) return;
      lastNotifTimeRef.current[conversationId] = now;
    }

    const notif = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: conversationId || 'flowteras',
      renotify: true,
    });

    notif.onclick = () => {
      window.focus();
      if (conversationId) {
        navigate(`/dashboard/conversations?id=${conversationId}`);
      }
      notif.close();
    };

    // Auto-close after 8 seconds
    setTimeout(() => notif.close(), 8000);
  }, [navigate]);

  const handleWSEvent = useCallback((data) => {
    if (data.event === 'notification') {
      // Real-time notification push
      setNotifications(prev => [data, ...prev].slice(0, 50));
      setUnreadCount(prev => prev + 1);
    } else if (data.event === 'new_message_received') {
      fetchNotifications();

      // Desktop notification for new customer message
      const prefs = notifPrefsRef.current;
      if (data.is_from_customer) {
        if (data.is_new_conversation && prefs.desktop_new_conversation) {
          showDesktopNotification(
            'New Customer Conversation',
            `Customer: ${data.customer_phone || 'Unknown'}\nMessage: "${data.message_preview || ''}"`,
            data.conversation_id
          );
        } else if (!data.is_new_conversation && prefs.desktop_customer_message) {
          showDesktopNotification(
            'New WhatsApp Message',
            `Customer: ${data.customer_phone || 'Unknown'}\nMessage: "${data.message_preview || ''}"`,
            data.conversation_id
          );
        }
      }
    } else if (data.event === 'conversation_escalated') {
      fetchNotifications();
      
      if (notifPrefsRef.current.desktop_conversation_escalated) {
        showDesktopNotification(
          'Conversation Escalated',
          `AI requires human assistance${data.reason ? ': ' + data.reason : ''}`,
          data.conversation_id
        );
      }
    } else if (data.event === 'conversation_updated' || data.event === 'channel_connected') {
      fetchNotifications();
    }
  }, [showDesktopNotification]);

  // ORPHAN USER REDIRECT: If user has no org and is not super admin, redirect to onboarding
  useEffect(() => {
    if (user && !user.organization_id && !user.is_super_admin) {
      // Only redirect if not already on onboarding page
      if (!location.pathname.includes('/onboarding')) {
        navigate('/dashboard/onboarding');
      }
    }
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    fetchNotifications();
    fetchOrganization();
    if (user?.is_super_admin) {
      fetchDebugStatus();
    }
  }, []);

  const fetchDebugStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/debug-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebugStatus(response.data);
    } catch (error) {
      // Not in debug mode or not super admin
      setDebugStatus(null);
    }
  };

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${API}/organizations/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganization(response.data);
    } catch (error) {
      console.error('Failed to fetch organization:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Get subscription plan from organization
  const subscriptionPlan = organization?.subscription_plan || 'trial';
  const isPaidPlan = subscriptionPlan !== 'trial';

  const mainNavItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: MessageSquare, label: 'Conversations', path: '/dashboard/conversations' },
    // Calendar visible only in Demo Mode OR when a real calendar is connected
    ...((demoMode.active || demoMode.hasRealCalendar)
      ? [{ icon: CalendarIcon, label: 'Calendar', path: '/dashboard/calendar' }]
      : []),
    { icon: FileQuestion, label: 'FAQ', path: '/dashboard/faq' },
    { icon: Workflow, label: 'Workflows', path: '/dashboard/workflows' },
    { icon: BarChart3, label: 'Analytics', path: '/dashboard/analytics' },
  ];

  // AI Setup only visible to owner/admin (not operators)
  const canAccessAISetup = user?.role !== 'operator' || user?.is_super_admin;

  const settingsNavItems = [
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
    { icon: Phone, label: 'Channels', path: '/dashboard/channels', ownerOnly: true },
    { icon: CreditCard, label: 'Billing', path: '/dashboard/billing', ownerOnly: true }, // Only Owner can access billing
    { icon: Users, label: 'Team', path: '/dashboard/users' },
    { icon: HelpCircle, label: 'Support', path: '/dashboard/support' },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/overview';
    }
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-card/95 backdrop-blur-sm border-b z-40">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-muted rounded-lg"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-logo">
              <div className="w-8 h-8 bg-emerald-700 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg hidden sm:block">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
            </Link>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="language-dropdown">
                  <Globe className="w-4 h-4" />
                  <span className="uppercase">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {['en', 'es', 'fr', 'de'].map(lang => (
                  <DropdownMenuItem 
                    key={lang} 
                    onClick={() => changeLanguage(lang)}
                    className={language === lang ? 'bg-muted' : ''}
                  >
                    {lang === 'en' ? 'English' : lang === 'es' ? 'Español' : lang === 'fr' ? 'Français' : 'Deutsch'}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-dropdown">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="p-3 border-b flex items-center justify-between">
                  <h3 className="font-semibold">Notifications</h3>
                  {unreadCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs h-7"
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/notifications/read-all`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          fetchNotifications();
                        } catch (e) { console.error(e); }
                      }}
                    >
                      Mark all read
                    </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 5).map(notif => (
                      <div 
                        key={notif.id} 
                        className={`p-3 border-b hover:bg-muted cursor-pointer ${!notif.is_read ? 'bg-emerald-50 dark:bg-emerald-900/10' : ''}`}
                        data-testid={`notification-item-${notif.id}`}
                        onClick={async () => {
                          // Mark as read
                          if (!notif.is_read) {
                            try {
                              await axios.put(`${API}/notifications/${notif.id}/read`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              fetchNotifications();
                            } catch (e) { console.error(e); }
                          }
                          // Navigate to conversation if applicable
                          const convId = notif.data?.conversation_id;
                          if (convId) {
                            navigate(`/dashboard/conversations?id=${convId}`);
                          }
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5">
                              {notif.type === 'new_message' && <MessageSquare className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                              {notif.type === 'new_conversation' && <Phone className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />}
                              {notif.type === 'escalation' && <Bell className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />}
                              <p className="font-medium text-sm">{notif.title}</p>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{notif.message}</p>
                            {notif.data?.conversation_id && (
                              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                                <ExternalLink className="h-3 w-3" />
                                Open conversation
                              </p>
                            )}
                          </div>
                          {!notif.is_read && (
                            <div className="w-2 h-2 bg-emerald-500 rounded-full mt-1.5 flex-shrink-0"></div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2" data-testid="user-menu">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
                    <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.organization_name}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="p-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard/account')} data-testid="menu-account">
                  <User className="w-4 h-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/dashboard/settings')} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" />
                  Organization Settings
                </DropdownMenuItem>
                {user?.is_super_admin && (
                  <DropdownMenuItem onClick={() => navigate('/dashboard/admin')} data-testid="menu-admin">
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Super Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-16 left-0 bottom-0 w-64 bg-card border-r z-30 transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 h-full flex flex-col overflow-y-auto">
          {/* Organization Badge */}
          <div className="mb-6 p-3 bg-muted rounded-lg flex-shrink-0">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-muted-foreground" />
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{user?.organization_name || 'Personal'}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
          
          {/* Main Navigation */}
          <nav className="flex-1 space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">Main</p>
            {mainNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive(item.path)
                    ? 'bg-emerald-700 text-white'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
            
            {/* AI Setup - Only for Owner/Admin */}
            {canAccessAISetup && (
              <div className="pt-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">AI</p>
                <Link
                  to="/dashboard/ai-setup"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive('/dashboard/ai-setup') || isActive('/dashboard/onboarding')
                      ? 'bg-emerald-700 text-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid="nav-ai-setup"
                >
                  <Sparkles className="w-5 h-5" />
                  <span className="font-medium">AI Setup</span>
                </Link>
              </div>
            )}
            
            <div className="pt-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">Settings</p>
              {settingsNavItems
                .filter(item => !item.ownerOnly || user?.role === 'owner' || user?.is_super_admin)
                .map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-emerald-700 text-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
            </div>
            
            {user?.is_super_admin && (
              <div className="pt-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-3 mb-2">Admin</p>
                <Link
                  to="/dashboard/admin"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive('/dashboard/admin')
                      ? 'bg-emerald-700 text-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid="nav-admin"
                >
                  <ShieldCheck className="w-5 h-5" />
                  <span className="font-medium">Super Admin</span>
                </Link>
                <Link
                  to="/dashboard/page-builder"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive('/dashboard/page-builder')
                      ? 'bg-emerald-700 text-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid="nav-page-builder"
                >
                  <Palette className="w-5 h-5" />
                  <span className="font-medium">Page Builder</span>
                </Link>
                <Link
                  to="/dashboard/test-lab"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive('/dashboard/test-lab')
                      ? 'bg-emerald-700 text-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid="nav-test-lab"
                >
                  <FlaskConical className="w-5 h-5" />
                  <span className="font-medium">Test Lab</span>
                </Link>
                <Link
                  to="/dashboard/admin/pipeline-debugger"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive('/dashboard/admin/pipeline-debugger')
                      ? 'bg-emerald-700 text-white'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid="nav-pipeline-debugger"
                >
                  <Activity className="w-5 h-5" />
                  <span className="font-medium">Pipeline Debugger</span>
                </Link>
              </div>
            )}
          </nav>
          
          {/* Plan Badge */}
          <div className="pt-4 border-t">
            {isPaidPlan ? (
              <div className="p-3 bg-gradient-to-r from-emerald-700 to-emerald-600 rounded-lg text-white">
                <p className="text-sm font-medium capitalize">{subscriptionPlan} Plan</p>
                <p className="text-xs opacity-80">Active subscription</p>
                <Link to="/dashboard/billing">
                  <Button size="sm" variant="secondary" className="w-full mt-2 text-emerald-700">
                    Manage
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="p-3 bg-gradient-to-r from-emerald-700 to-lime-600 rounded-lg text-white">
                <p className="text-sm font-medium">Trial Plan</p>
                <p className="text-xs opacity-80">Upgrade for more features</p>
                <Link to="/dashboard/billing">
                  <Button size="sm" variant="secondary" className="w-full mt-2 text-emerald-700">
                    Upgrade
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-6">
          <DemoModeBanner />
          {/* SUPER ADMIN DEBUG MODE BANNER - RED */}
          {debugStatus?.debug_mode && (
            <div 
              className="mb-4 p-3 bg-red-100 border-2 border-red-400 rounded-lg flex items-center justify-between"
              data-testid="debug-mode-banner"
            >
              <div className="flex items-center gap-3">
                <FlaskConical className="w-5 h-5 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800 text-sm">
                    DEBUG MODE — Viewing: {debugStatus.debug_org_name}
                  </p>
                  <p className="text-xs text-red-600">
                    You are viewing data as organization member. Super admin features hidden.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-red-400 text-red-700 hover:bg-red-200"
                onClick={async () => {
                  try {
                    const response = await axios.post(`${API}/admin/clear-org-context`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    // Update token - need to use the new token
                    if (response.data.access_token) {
                      localStorage.setItem('token', response.data.access_token);
                    }
                    setDebugStatus(null);
                    window.location.reload();
                  } catch (e) {
                    console.error('Failed to clear debug context:', e);
                  }
                }}
              >
                Exit Debug Mode
              </Button>
            </div>
          )}
          <EmailVerificationBanner />
          <Outlet />
        </div>
      </main>

      {/* Onboarding Welcome Modal - Shows for first-time users */}
      <OnboardingWelcomeModal />
    </div>
  );
};

export default DashboardLayout;
